/**
 * Le rang de la semaine, en haut à droite (Kayla, 07-10-2026) : « dans le
 * cockpit, en haut à droite, il voit son rang » — et « quand il gagne un rang,
 * parce qu'il a fait une vente, une petite animation pour dire qu'il vient de
 * grimper ».
 *
 * La pastille n'apparaît que pour qui est CLASSÉ cette semaine (un chatteur qui
 * a vendu) ; un clic ouvre Personnel › Classement. La montée se lit sur le
 * document Convex `classement.semaine`, qui change en temps réel : même règle
 * que l'annonce de #rang (`_lib/classement.ts::montees`), comparée au dernier
 * rang que CE navigateur a vu pour cette semaine (localStorage) — une page
 * rechargée ne refait pas la fête, un nouveau lundi repart de zéro.
 *
 * 🏆 Monter sur le podium (places 1 à 3) déclenche la grande fête
 * (`PodiumCelebration`, Kayla 09-10-2026) ; ses sons ne jouent que dans UNE
 * fenêtre de la personne (`reclamerSon`), même cockpit ouvert deux fois.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "convex/react";
import { useSetAtom } from "jotai";
import { AnimatePresence, motion } from "framer-motion";

import { api } from "@/../convex/_generated/api";
import { montees, place } from "@/../convex/_lib/classement";
import { useActiveAccount } from "@/core/auth/activeAccount";
import { layoutAtom, mobileActiveInstanceIdAtom } from "@/core/state";
import { useRegieSounds } from "@/shell/sounds";
import { useReclamerSon } from "@/core/pwa/usePaymentSound";
import { PodiumCelebration, type FetePodium } from "@/shell/PodiumCelebration";
import { dollars } from "@/panels/personnel/classement/ClassementOnglet";
import { useClassementSemaine } from "@/panels/personnel/classement/useClassementSemaine";
import { addPanel, findGroupContaining, listAllPanelInstances, setActiveTab, setPanelParams } from "@/shell/layout";
import { Button, Carte, Etiquette, Infobulle } from "@/shared/ui";
import { ArrowUp, Trophy } from "@/shared/ui/icons";

const CLE = (accountId: string) => `classement:vu:${accountId}`;

function lireVu(accountId: string): { cycle: string; rang: number } | null {
  try {
    return JSON.parse(localStorage.getItem(CLE(accountId)) ?? "null");
  } catch {
    return null;
  }
}

function noterVu(accountId: string, cycle: string, rang: number): void {
  try {
    localStorage.setItem(CLE(accountId), JSON.stringify({ cycle, rang }));
  } catch {
    /* mode privé : on refera peut-être la fête, rien de grave */
  }
}

/** Ouvre Personnel sur l'onglet Classement — le panneau existant s'il est là
 *  (kiosque compris : `layoutAtom` écrit dans l'override du kiosque). */
export function useOuvrirClassement() {
  const setLayout = useSetAtom(layoutAtom);
  const setActifMobile = useSetAtom(mobileActiveInstanceIdAtom);
  return useCallback(() => {
    let id: string | null = null;
    setLayout((prev) => {
      const existant = listAllPanelInstances(prev).find((p) => p.panelId === "personnel");
      if (!existant) {
        try {
          const r = addPanel(prev, "personnel", { kind: "active-group" }, { params: { tab: "classement" } });
          id = r.instanceId;
          return r.layout;
        } catch {
          return prev;
        }
      }
      id = existant.id;
      let next = setPanelParams(prev, existant.id, {
        ...((existant.params as Record<string, unknown> | undefined) ?? {}),
        tab: "classement",
      });
      const groupe = findGroupContaining(next, existant.id);
      if (groupe) next = setActiveTab(next, groupe.id, groupe.panels.findIndex((p) => p.id === existant.id));
      return next;
    });
    if (id) setActifMobile(id);
  }, [setLayout, setActifMobile]);
}

export function RangBadge({
  variant = "bar",
}: {
  /** "rail" = pastille empilée (trophée sur le rang), pour le rail gauche du
   *  kiosque, qui n'a plus de barre du haut. */
  variant?: "bar" | "rail";
} = {}) {
  const { account } = useActiveAccount();
  const data = useClassementSemaine(account?._id ?? null);
  const ouvrir = useOuvrirClassement();
  const { play } = useRegieSounds();
  const [fete, setFete] = useState<{ id: number; rang: number } | null>(null);
  const [podium, setPodium] = useState<FetePodium | null>(null);
  const prefs = useQuery(api.paymentNotifications.getMyNotificationPrefs);
  const sonVoulu = prefs?.paymentSoundEnabled ?? true;
  const reclamerSon = useReclamerSon();

  const rang = data?.monRang ?? null;
  const cycle = data?.cycle ?? null;
  const accountId = account?._id ?? null;
  // Lu au moment de la montée, sans relancer l'effet à chaque mise à jour.
  const lignes = useRef(data?.lignes ?? []);
  lignes.current = data?.lignes ?? [];

  useEffect(() => {
    if (!accountId || !cycle || rang === null) return;
    const vu = lireVu(accountId);
    const avant = vu && vu.cycle === cycle ? [{ workosUserId: "moi", brutCents: 0, rang: vu.rang }] : [];
    // Premier passage de la semaine sur ce navigateur : on note sans fêter
    // (sinon chaque rechargement du lundi ferait « tu montes »).
    if (vu && montees(avant, [{ workosUserId: "moi", brutCents: 0, rang }]).length > 0) {
      if (rang <= 3) {
        const moi = lignes.current.find((l) => l.moi);
        const devant = lignes.current.find((l) => l.rang === rang + 1 && !l.moi)?.nom ?? null;
        const prime = moi && moi.primeCents > 0 ? dollars(moi.primeCents) : null;
        const id = Date.now();
        void (async () => {
          const sonIci = sonVoulu && (await reclamerSon(`rang:${cycle}:${rang}`));
          setPodium({ id, rang: rang as 1 | 2 | 3, devant, prime, sonIci });
        })();
      } else {
        setFete({ id: Date.now(), rang });
        play("win");
      }
    }
    if (!vu || vu.cycle !== cycle || vu.rang !== rang) noterVu(accountId, cycle, rang);
  }, [accountId, cycle, rang, play, reclamerSon, sonVoulu]);

  return (
    <>
      <AnimatePresence>
        {rang !== null && (
          <motion.span
            initial={{ scale: 0.6, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.6, opacity: 0 }}
            className="inline-flex"
          >
            {/* Un `Button` : à la marque sur le podium (places 1 à 3), de verre sinon. */}
            <Infobulle texte="Ton classement de la semaine" cote={variant === "rail" ? "droite" : "bas"}>
              <Button
                variant={rang <= 3 ? "accent" : "secondary"}
                size="md"
                onClick={ouvrir}
                aria-label="Ton classement de la semaine"
                data-testid="rang-badge"
                className={variant === "rail" ? "h-11 w-9 flex-col gap-0.5 px-0 py-0 tabular-nums" : "h-9 tabular-nums"}
              >
                <Trophy className="h-3.5 w-3.5" />
                <motion.span key={rang} initial={{ y: 8, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
                  #{rang}
                </motion.span>
              </Button>
            </Infobulle>
          </motion.span>
        )}
      </AnimatePresence>
      <RangMonte fete={fete} onFin={() => setFete(null)} onOuvrir={ouvrir} />
      <PodiumCelebration fete={podium} onFin={() => setPodium(null)} onOuvrir={ouvrir} />
    </>
  );
}

/** La fête de la montée : une carte au centre, le nouveau rang qui arrive par
 *  le bas, un halo vert soufflé — le même vocabulaire que la pop-up de vente
 *  (`VenteCelebration`), en plus court. Elle se ferme seule. */
export function RangMonte({
  fete,
  onFin,
  onOuvrir,
}: {
  fete: { id: number; rang: number } | null;
  onFin: () => void;
  onOuvrir: () => void;
}) {
  useEffect(() => {
    if (!fete) return;
    const t = setTimeout(onFin, 4_500);
    return () => clearTimeout(t);
  }, [fete, onFin]);

  return (
    <AnimatePresence>
      {fete && (
        <motion.div
          key={fete.id}
          className="pointer-events-none fixed inset-0 z-[347] flex items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          role="status"
          aria-live="polite"
          data-testid="rang-monte"
        >
          <motion.div
            aria-hidden
            className="absolute h-[110vmax] w-[110vmax] rounded-full regie-round"
            style={{
              background:
                "radial-gradient(circle, color-mix(in srgb, var(--color-mark) 24%, transparent) 0%, color-mix(in srgb, var(--color-mark) 6%, transparent) 30%, transparent 55%)",
            }}
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: [0, 1, 0.5], scale: [0.7, 1.04, 1] }}
            transition={{ duration: 0.9, ease: "easeOut" }}
          />
          <motion.div
            className="pointer-events-auto relative w-[min(90vw,22rem)]"
            initial={{ scale: 0.6, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, y: -10, opacity: 0 }}
            transition={{ type: "spring", stiffness: 420, damping: 20, mass: 0.7 }}
          >
            {/* La carte entière est un bouton (Carte cliquable) : elle ouvre le classement. */}
            <Carte
              onClick={() => {
                onOuvrir();
                onFin();
              }}
              remplissage="lg"
              className="flex flex-col items-center gap-2 bg-elevated px-8 pb-7 pt-8 text-center shadow-2xl"
            >
              <Etiquette icone={<ArrowUp className="h-4 w-4" />}>Tu montes !</Etiquette>
              <div className="relative flex h-28 w-28 items-center justify-center overflow-hidden">
                <motion.span
                  className={
                    "regie-round flex h-24 w-24 items-center justify-center text-5xl font-black tabular-nums " +
                    (fete.rang <= 3 ? "bg-mark text-mark-fg" : "bg-sunken text-fg")
                  }
                  initial={{ y: 90, rotate: -12 }}
                  animate={{ y: 0, rotate: 0 }}
                  transition={{ type: "spring", stiffness: 300, damping: 14, delay: 0.15 }}
                >
                  #{fete.rang}
                </motion.span>
              </div>
              <span className="text-2xl font-black text-fg">
                {fete.rang === 1 ? "1re place !" : `${place(fete.rang)} place`}
              </span>
              <span className="text-sm text-muted">
                {fete.rang <= 3 ? "Tu es sur le podium de la semaine." : "Continue, le podium approche."}
              </span>
            </Carte>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
